/*
 * Compact Karl overview for the narrow task pane: answered and Confidence
 * counts per rule, the assists left, the only-Likely-and-Uncertain filter, the
 * bulk apply of Sure verdicts, and an undo per applied finding. Counts come
 * from the verdicts the panel already renders, so they cannot disagree with the
 * badges on the cards.
 */
import { useEffect, useState } from "react";
import { Button, Checkbox, Text } from "@fluentui/react-components";
import { useTranslation } from "../../i18n/useTranslation";
import { formatKarlAssists, type KarlOverviewRow } from "../../karl/karlLogic";
import type { KarlAssists } from "../../state/types";
import { KarlAvatar } from "./KarlAvatar";

export interface KarlAppliedEntry {
  index: number;
  label: string;
}

export function KarlOverview({
  rows,
  assists,
  sureFindings,
  sureChanges,
  skipSure,
  onSkipSureChange,
  onAskRule,
  ruleCount = 0,
  onAskAll,
  allCount,
  applyDisabled,
  applying,
  onApplySure,
  applied,
  onUndo,
}: {
  rows: KarlOverviewRow[];
  assists: KarlAssists | null;
  sureFindings: number;
  sureChanges: number;
  skipSure: boolean;
  // eslint-disable-next-line no-unused-vars -- the name documents the callback argument
  onSkipSureChange: (checked: boolean) => void;
  onAskRule?: () => void;
  ruleCount?: number;
  onAskAll: () => void;
  allCount: number;
  applyDisabled: boolean;
  applying: boolean;
  onApplySure: () => void;
  applied: KarlAppliedEntry[];
  // eslint-disable-next-line no-unused-vars -- the name documents the callback argument
  onUndo: (index: number) => void;
}): JSX.Element {
  const { t, format } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const numberFormat = new Intl.NumberFormat();

  useEffect(() => {
    if (sureFindings === 0) setConfirming(false);
  }, [sureFindings]);

  const hasAnswers = rows.some((row) => row.answered > 0);

  return (
    <section className="karl-overview" data-karl-overview aria-label={t.karlOverviewTitle}>
      <header className="karl-overview__header">
        <KarlAvatar animated={false} />
        <Text size={200} className="karl-overview__hint">
          {t.karlOverviewHint}
        </Text>
      </header>

      {assists ? (
        <Text size={200} className="karl-overview__assists" data-karl-assists>
          {formatKarlAssists(assists, t, (value) => numberFormat.format(value))}
        </Text>
      ) : null}

      <ul className="karl-overview__rules">
        {rows.map((row) => (
          <li key={row.ruleId} className="karl-overview__rule" data-karl-rule={row.ruleId}>
            <span className="karl-overview__rule-title">{row.title}</span>
            <span className="karl-overview__rule-count">
              {format(t.karlOverviewRuleCount, {
                answered: numberFormat.format(row.answered),
                total: numberFormat.format(row.total),
              })}
            </span>
            {row.answered > 0 ? (
              <span className="karl-overview__chips">
                <span className="karl-overview__chip" data-karl-count="sure">
                  {`${t.karlCountSure} ${numberFormat.format(row.sure)}`}
                </span>
                <span className="karl-overview__chip" data-karl-count="likely">
                  {`${t.karlCountLikely} ${numberFormat.format(row.likely)}`}
                </span>
                <span className="karl-overview__chip" data-karl-count="uncertain">
                  {`${t.karlCountUncertain} ${numberFormat.format(row.uncertain)}`}
                </span>
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      {!hasAnswers ? (
        <Text size={200} className="karl-overview__empty" data-karl-overview-empty>
          {t.karlOverviewNoAnswers}
        </Text>
      ) : null}

      <Checkbox
        data-karl-filter-toggle
        checked={skipSure}
        onChange={(_event, data) => onSkipSureChange(Boolean(data.checked))}
        label={t.karlFilterLabel}
        aria-describedby="karl-filter-hint"
      />
      <Text size={200} id="karl-filter-hint" className="karl-overview__hint">
        {t.karlFilterHint}
      </Text>

      <div className="karl-overview__actions">
        {onAskRule && ruleCount > 0 ? (
          <Button appearance="secondary" size="small" onClick={onAskRule} disabled={applying}>
            {format(t.karlAskRule, { count: numberFormat.format(ruleCount) })}
          </Button>
        ) : null}
        <Button appearance="secondary" size="small" onClick={onAskAll} disabled={applying || allCount === 0}>
          {format(t.karlAskAll, { count: numberFormat.format(allCount) })}
        </Button>
      </div>

      {sureFindings > 0 ? (
        <div className="karl-overview__apply" data-karl-apply>
          <Text size={200} className="karl-overview__hint">
            {t.karlApplyHint}
          </Text>
          {confirming ? (
            <div className="karl-overview__apply-confirm" role="group" aria-label={t.karlApplyConfirmLabel}>
              <Text size={200} data-karl-apply-confirm>
                {format(t.karlApplyConfirm, {
                  findings: numberFormat.format(sureFindings),
                  changes: numberFormat.format(sureChanges),
                })}
              </Text>
              <div className="karl-overview__actions">
                <Button
                  appearance="primary"
                  size="small"
                  onClick={() => {
                    setConfirming(false);
                    onApplySure();
                  }}
                  disabled={applyDisabled}
                >
                  {format(t.karlApplyConfirmAction, { count: numberFormat.format(sureFindings) })}
                </Button>
                <Button appearance="secondary" size="small" onClick={() => setConfirming(false)}>
                  {t.karlApplyCancel}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              appearance="primary"
              size="small"
              onClick={() => setConfirming(true)}
              disabled={applyDisabled}
            >
              {format(t.karlApplyAction, { count: numberFormat.format(sureFindings) })}
            </Button>
          )}
        </div>
      ) : null}

      {applied.length > 0 ? (
        <div className="karl-overview__applied" data-karl-applied>
          <Text size={200} className="karl-overview__applied-title">
            {t.karlAppliedTitle}
          </Text>
          <ul>
            {applied.map((entry) => (
              <li key={entry.index}>
                <span>{entry.label}</span>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => onUndo(entry.index)}
                  disabled={applying}
                >
                  {t.karlUndo}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Text size={200} className="karl-overview__disclaimer" data-karl-disclaimer>
        {t.karlDisclaimer}
      </Text>
    </section>
  );
}
